import React from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Icon,
  Flex,
  useBreakpointValue,
} from '@chakra-ui/react';
import { FaCheck } from 'react-icons/fa';
import { BUILD_STAGE_LABELS, BUILD_STAGE_ORDER, type BuildStage } from '../types';

const PROPERTY_PRIMARY = '#0D7F6E';
const NAVY = '#141C49';

interface BuildStageTrackerProps {
  currentStage: BuildStage;
}

export const BuildStageTracker: React.FC<BuildStageTrackerProps> = ({ currentStage }) => {
  const currentIndex = BUILD_STAGE_ORDER.indexOf(currentStage);
  const labelFontSize = useBreakpointValue({ base: '2xs', md: 'xs' });
  const circleSizePx = useBreakpointValue({ base: 28, md: 36 });

  return (
    <Box
      overflowX="auto"
      pb={2}
      sx={{ '&::-webkit-scrollbar': { height: '4px' }, '&::-webkit-scrollbar-thumb': { bg: 'gray.300', borderRadius: '2px' } }}
    >
      <HStack
        spacing={0}
        align="flex-start"
        minW={{ base: '600px', md: 'auto' }}
        px={2}
      >
        {BUILD_STAGE_ORDER.map((stage, idx) => {
          const isComplete = idx < currentIndex;
          const isActive = idx === currentIndex;
          const isUpcoming = idx > currentIndex;
          const isLast = idx === BUILD_STAGE_ORDER.length - 1;
          const size = circleSizePx ?? 36;

          return (
            <React.Fragment key={stage}>
              <VStack spacing={2} align="center" flex={isLast ? 'none' : undefined} minW={`${size + 8}px`}>
                {/* Circle */}
                <Flex
                  w={`${size}px`}
                  h={`${size}px`}
                  borderRadius="full"
                  align="center"
                  justify="center"
                  bg={isComplete ? 'green.500' : isActive ? PROPERTY_PRIMARY : 'white'}
                  border="2px solid"
                  borderColor={isComplete ? 'green.500' : isActive ? PROPERTY_PRIMARY : 'gray.300'}
                  flexShrink={0}
                  boxShadow={isActive ? `0 0 0 4px rgba(13,127,110,0.15)` : 'none'}
                  transition="all 0.2s"
                >
                  {isComplete ? (
                    <Icon as={FaCheck} color="white" boxSize={size > 30 ? 3.5 : 3} />
                  ) : (
                    <Text
                      fontSize={size > 30 ? 'sm' : 'xs'}
                      fontWeight="bold"
                      color={isActive ? 'white' : 'gray.400'}
                    >
                      {idx + 1}
                    </Text>
                  )}
                </Flex>
                {/* Label */}
                <Text
                  fontSize={labelFontSize}
                  fontWeight={isActive ? 'bold' : 'normal'}
                  color={isActive ? NAVY : isComplete ? 'green.600' : 'gray.400'}
                  textAlign="center"
                  lineHeight="1.2"
                  maxW="60px"
                >
                  {BUILD_STAGE_LABELS[stage]}
                </Text>
              </VStack>

              {/* Connector line */}
              {!isLast && (
                <Box
                  flex={1}
                  h="2px"
                  mt={`${(size / 2) - 1}px`}
                  bg={isComplete ? 'green.400' : 'gray.200'}
                  transition="background 0.2s"
                  minW="12px"
                />
              )}
            </React.Fragment>
          );
        })}
      </HStack>
    </Box>
  );
};
